// @ts-nocheck
// =============================================================================
//  KitchenControl — Whiteboard (gedeeld "op / bijna op"-bord)
//  Gedeeld tussen MEP en Recepten. Iedereen mag schrijven, ongeacht rol.
//  Tabel: whiteboard { id, tenant_id, tekst, prioriteit, toegevoegd_door, created_at }
//
//  Exports:
//   • useWhiteboard(sb, tenantId, supabaseClient) → { items, artikelen, voegToe, verwijder }
//   • WhiteboardButton({ count, onClick })  — pill voor de (donkere) module-header
//   • WhiteboardSheet({ open, onClose, items, artikelen, voegToe, verwijder })
//   • Whiteboard({ sb, tenantId, supabaseClient }) — alles-in-één (knop + sheet)
// =============================================================================
import { useState, useEffect, useRef, useCallback } from "react";
import { ClipboardList } from "lucide-react";

const C = {
  bg2:    "#FFFFFF",
  bg3:    "#F1F5F9",
  txt:    "#0F172A",
  txt2:   "#475569",
  txt3:   "#94A3B8",
  bdr:    "#E2E8F0",
  pri:    "#2563EB",
  op:     "#DC2626", opBg: "#FEF2F2", opBd: "#FECACA",
  bijna:  "#EA580C", bijnaBg: "#FFF7ED", bijnaBd: "#FED7AA",
};

function fmtTijd(iso) {
  try { return new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOK — laadt + synct het bord, plus artikel-suggesties
// ─────────────────────────────────────────────────────────────────────────────
export function useWhiteboard(sb, tenantId, supabaseClient) {
  const [items, setItems] = useState([]);
  const [artikelen, setArtikelen] = useState([]);
  const [door, setDoor] = useState("");
  const artikelenRef = useRef([]);
  useEffect(() => { artikelenRef.current = artikelen; }, [artikelen]);

  // Huidige gebruiker (voor "door wie") — uit de lokale sessie, geen netwerk.
  // Voorkeur: de ingevulde schermnaam (roepnaam). Pas als die er niet is,
  // vallen we terug op het deel vóór de @ van het e-mailadres.
  useEffect(() => {
    if (!supabaseClient) return;
    let actief = true;
    supabaseClient.auth.getSession().then(({ data }) => {
      if (!actief) return;
      const user = data?.session?.user;
      const roepnaam = (user?.user_metadata?.roepnaam || "").trim();
      const email = user?.email || "";
      const uitEmail = email ? (email.split("@")[0] || email) : "";
      setDoor(roepnaam || uitEmail);
    }).catch(() => {});
    return () => { actief = false; };
  }, [supabaseClient]);

  // Bord laden
  const laad = useCallback(async () => {
    if (!sb || !tenantId) return;
    const rows = await sb.select("whiteboard", `tenant_id=eq.${tenantId}&select=*&order=created_at.desc`);
    if (Array.isArray(rows)) setItems(rows);
  }, [sb, tenantId]);
  useEffect(() => { laad(); }, [laad]);

  // Artikel-suggesties (gedeelde 'artikelen'-lijst)
  useEffect(() => {
    if (!sb || !tenantId) return;
    let actief = true;
    (async () => {
      try {
        const rows = await sb.select("artikelen", `tenant_id=eq.${tenantId}&select=data&limit=1`);
        if (actief && Array.isArray(rows?.[0]?.data)) setArtikelen(rows[0].data);
      } catch { /* stil — suggesties beginnen leeg */ }
    })();
    return () => { actief = false; };
  }, [sb, tenantId]);

  // Realtime: één postgres_changes-handler per kanaal.
  useEffect(() => {
    if (!supabaseClient || !tenantId) return;
    const ch = supabaseClient
      .channel(`whiteboard_${tenantId}_${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "whiteboard", filter: `tenant_id=eq.${tenantId}` },
        (p) => {
          if (p.eventType === "INSERT") {
            const n = p.new;
            if (n) setItems(prev => prev.some(i => i.id === n.id) ? prev : [n, ...prev]);
            return;
          }
          if (p.eventType === "UPDATE") {
            const n = p.new;
            if (n) setItems(prev => prev.map(i => i.id === n.id ? n : i));
            return;
          }
          if (p.eventType === "DELETE") {
            const id = p.old?.id;
            if (id) setItems(prev => prev.filter(i => i.id !== id));
          }
        })
      .subscribe();
    return () => { supabaseClient.removeChannel(ch); };
  }, [supabaseClient, tenantId]);

  // Poll-fallback (als realtime niet beschikbaar is)
  useEffect(() => {
    if (!sb || !tenantId) return;
    const t = setInterval(laad, 8000);
    return () => clearInterval(t);
  }, [sb, tenantId, laad]);

  // Onthoud een nieuw artikel in de gedeelde lijst (groeit de suggesties)
  const onthoudArtikel = useCallback(async (naam) => {
    const n = (naam || "").trim();
    if (!n || !sb || !tenantId) return;
    const huidig = artikelenRef.current || [];
    if (huidig.some(a => a.toLowerCase() === n.toLowerCase())) return;
    const lijst = [...huidig, n].sort((a, b) => a.localeCompare(b, "nl"));
    setArtikelen(lijst);
    artikelenRef.current = lijst;
    try {
      const exists = await sb.select("artikelen", `tenant_id=eq.${tenantId}&select=tenant_id&limit=1`);
      if (exists?.length) await sb.update("artikelen", `tenant_id=eq.${tenantId}`, { data: lijst, updated_at: new Date().toISOString() });
      else await sb.insert("artikelen", { tenant_id: tenantId, data: lijst, updated_at: new Date().toISOString() });
    } catch { /* stil */ }
  }, [sb, tenantId]);

  // Toevoegen (optimistisch + insert)
  const voegToe = useCallback(async (tekst, prioriteit) => {
    const naam = (tekst || "").trim();
    if (!naam || !sb || !tenantId) return;
    const tmpId = `tmp-${Date.now()}-${Math.floor(Math.random() * 9999)}`;
    const tijdelijk = { id: tmpId, tenant_id: tenantId, tekst: naam, prioriteit: prioriteit || "bijna_op", toegevoegd_door: door, created_at: new Date().toISOString() };
    setItems(prev => [tijdelijk, ...prev]);
    const row = await sb.insert("whiteboard", { tenant_id: tenantId, tekst: naam, prioriteit: prioriteit || "bijna_op", toegevoegd_door: door });
    setItems(prev => row && row.id ? prev.map(i => i.id === tmpId ? row : i) : prev.filter(i => i.id !== tmpId));
    onthoudArtikel(naam);
  }, [sb, tenantId, door, onthoudArtikel]);

  // Wegstrepen
  const verwijder = useCallback(async (id) => {
    setItems(prev => prev.filter(i => i.id !== id));
    if (sb && id && !String(id).startsWith("tmp-")) await sb.remove("whiteboard", `id=eq.${id}`);
  }, [sb]);

  return { items, artikelen, voegToe, verwijder };
}

// ─────────────────────────────────────────────────────────────────────────────
// KNOP — voor de donkere module-header
// ─────────────────────────────────────────────────────────────────────────────
export function WhiteboardButton({ count = 0, onClick }) {
  return (
    <button onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.2)", color: "#fff", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 600, whiteSpace: "nowrap" }}>
      <ClipboardList size={14} /> Whiteboard
      {count > 0 && (
        <span style={{ background: C.op, color: "#fff", borderRadius: 999, fontSize: 11, fontWeight: 700, minWidth: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>{count}</span>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BOTTOM-SHEET (controlled)
// ─────────────────────────────────────────────────────────────────────────────
function tgStyle(sel, kind) {
  const base = { flex: 1, border: `1px solid ${C.bdr}`, background: "#fff", borderRadius: 9, padding: "8px", fontSize: 12.5, fontWeight: 700, color: C.txt2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit" };
  if (!sel) return base;
  if (kind === "op") return { ...base, background: C.opBg, borderColor: C.opBd, color: C.op };
  return { ...base, background: C.bijnaBg, borderColor: C.bijnaBd, color: C.bijna };
}

export function WhiteboardSheet({ open, onClose, items = [], artikelen = [], voegToe, verwijder, regelMep }) {
  // KC_UI_V2_WHITEBOARD_VOLWASSEN_PREVIEW
  const [tekst, setTekst] = useState("");
  const [prio, setPrio] = useState("bijna_op");
  const [toonSug, setToonSug] = useState(false);
  const [sugIdx, setSugIdx] = useState(-1); // gemarkeerde suggestie voor toetsenbord
  const [editId, setEditId] = useState(null);
  const [editTekst, setEditTekst] = useState("");
  const [previewAanpassingen, setPreviewAanpassingen] = useState({});
  const inputRef = useRef();

  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    const t = setTimeout(() => inputRef.current?.focus(), 150);
    return () => { window.removeEventListener("keydown", h); clearTimeout(t); };
  }, [open, onClose]);

  if (!open) return null;

  const q = tekst.trim().toLowerCase();
  const sug = q ? (artikelen || []).filter(a => a.toLowerCase().includes(q)).slice(0, 5) : [];

  const add = (naam) => {
    const n = (naam ?? tekst).trim();
    if (!n) return;
    voegToe?.(n, prio);
    setTekst("");
    setToonSug(false);
    setSugIdx(-1);
    inputRef.current?.focus();
  };

  const tekstVan = (it) => previewAanpassingen[it.id] || it.tekst || "";
  const startAanpassen = (it) => {
    setEditId(it.id);
    setEditTekst(tekstVan(it));
  };
  const bewaarPreviewAanpassing = (it) => {
    const n = editTekst.trim();
    if (!n) return;
    setPreviewAanpassingen(cur => ({ ...cur, [it.id]: n }));
    setEditId(null);
    setEditTekst("");
  };
  const printWhiteboard = () => {
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, ch => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#039;"}[ch]));
    const mep = items.filter(it => it.bron === "mep" || it.readonly === true);
    const hand = items.filter(it => !(it.bron === "mep" || it.readonly === true));
    const rows = (lijst, toonStatus = false) => lijst.map(it => `<tr><td>${esc(tekstVan(it))}</td>${toonStatus ? `<td>${esc(it.prioriteit === "op" ? "Op" : "Bijna op")}</td>` : ""}<td class="check">□</td></tr>`).join("");
    const w = window.open("", "_blank", "width=900,height=720");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>KitchenControl Whiteboard</title><style>body{font-family:Arial,sans-serif;color:#0f172a;margin:32px}h1{font-size:24px;margin:0 0 4px}.sub{color:#64748b;margin-bottom:26px}h2{font-size:15px;margin:24px 0 8px;border-bottom:2px solid #0f172a;padding-bottom:6px}table{width:100%;border-collapse:collapse}td{padding:10px 8px;border-bottom:1px solid #e2e8f0;font-size:14px}.check{width:40px;text-align:center;font-size:20px}.foot{margin-top:28px;color:#94a3b8;font-size:11px}@media print{body{margin:15mm}}</style></head><body><h1>KitchenControl — Whiteboard</h1><div class="sub">Openstaande bestel- en voorraadartikelen · ${new Date().toLocaleDateString("nl-NL")}</div>${mep.length ? `<h2>Uit Mise en Place (${mep.length})</h2><table>${rows(mep, false)}</table>` : ""}${hand.length ? `<h2>Handmatig toegevoegd (${hand.length})</h2><table>${rows(hand, true)}</table>` : ""}<div class="foot">Afgedrukt vanuit KitchenControl</div><script>window.onload=()=>window.print()<\/script></body></html>`);
    w.document.close();
  };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 14 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#fff", width: "100%", maxWidth: 760, borderRadius: 16, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 18px 55px rgba(15,23,42,.24)", fontFamily: "system-ui,-apple-system,sans-serif" }}>
        <div style={{ width: 38, height: 4, background: C.bdr, borderRadius: 2, margin: "10px auto 4px" }} />

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "8px 18px 12px" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.txt, display: "flex", alignItems: "center", gap: 7 }}>
              <ClipboardList size={18} color={C.pri} /> Whiteboard
            </div>
            <div style={{ fontSize: 12.5, color: C.txt3, marginTop: 3 }}>Bestellen en voorraad · {items.length} open · {items.filter(i => i.bron === "mep" || i.readonly === true).length} uit MEP</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <button onClick={printWhiteboard} disabled={!items.length} style={{ background: C.bg2, border: `1px solid ${C.bdr}`, color: items.length ? C.txt2 : C.txt3, borderRadius: 8, padding: "7px 11px", fontSize: 12, fontWeight: 750, cursor: items.length ? "pointer" : "default", whiteSpace: "nowrap" }}>Printen</button>
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: C.txt3, cursor: "pointer", lineHeight: 1, padding: "0 4px" }}>×</button>
          </div>
        </div>

        {/* Invoer */}
        <div style={{ padding: "0 18px 12px", position: "relative" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <input ref={inputRef} value={tekst} autoComplete="off" placeholder="Bijv. olijfolie…"
              onChange={e => { setTekst(e.target.value); setToonSug(true); setSugIdx(-1); }}
              onFocus={() => setToonSug(true)}
              onKeyDown={e => {
                if (e.key === "ArrowDown") { e.preventDefault(); if (sug.length) { setToonSug(true); setSugIdx(i => (i + 1) % sug.length); } }
                else if (e.key === "ArrowUp") { e.preventDefault(); if (sug.length) { setToonSug(true); setSugIdx(i => (i <= 0 ? sug.length - 1 : i - 1)); } }
                else if (e.key === "Enter") { e.preventDefault(); if (toonSug && sugIdx >= 0 && sug[sugIdx]) add(sug[sugIdx]); else add(); }
                else if (e.key === "Escape") { if (toonSug && sug.length) { e.stopPropagation(); setToonSug(false); setSugIdx(-1); } }
              }}
              style={{ flex: 1, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: "12px 13px", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
            <button onClick={() => add()} style={{ background: C.pri, color: "#fff", border: "none", borderRadius: 10, padding: "0 16px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Toevoegen</button>
          </div>

          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button onClick={() => setPrio("op")} style={tgStyle(prio === "op", "op")}>
              <span style={{ width: 9, height: 9, borderRadius: 999, background: C.op }} /> Op
            </button>
            <button onClick={() => setPrio("bijna_op")} style={tgStyle(prio === "bijna_op", "bijna")}>
              <span style={{ width: 9, height: 9, borderRadius: 999, background: C.bijna }} /> Bijna op
            </button>
          </div>

          {toonSug && sug.length > 0 && (
            <div style={{ position: "absolute", left: 18, right: 18, marginTop: 4, background: "#fff", border: `1px solid ${C.bdr}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(15,23,42,.12)", overflow: "hidden", zIndex: 5 }}>
              {sug.map((a, i) => (
                <div key={a} onMouseDown={() => add(a)} onMouseEnter={() => setSugIdx(i)}
                  style={{ padding: "11px 13px", fontSize: 14, cursor: "pointer", borderBottom: `1px solid ${C.bg3}`, background: i === sugIdx ? C.bg3 : "#fff" }}>{a}</div>
              ))}
            </div>
          )}
        </div>

        {/* Lijst */}
        <div style={{ padding: "4px 18px 22px", overflowY: "auto" }}>
          {items.length === 0 ? (
            <div style={{ color: C.txt3, fontSize: 13.5, textAlign: "center", padding: "26px 0" }}>Niets op het bord — alles op voorraad ✓</div>
          ) : items.map(it => {
            const mepItem = it.bron === "mep" || it.readonly === true;
            const op = it.prioriteit === "op";
            const tijd = fmtTijd(it.created_at);
            const editing = editId === it.id;
            return (
              <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 0", borderBottom: `1px solid ${C.bg3}`, flexWrap: "wrap" }}>
                <span style={{ width: 10, height: 10, borderRadius: 999, background: mepItem ? C.pri : op ? C.op : C.bijna, flexShrink: 0 }} />
                <div style={{ flex: "1 1 250px", minWidth: 0 }}>
                  {editing ? (
                    <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                      <input autoFocus value={editTekst} onChange={e => setEditTekst(e.target.value)} onKeyDown={e => { if (e.key === "Enter") bewaarPreviewAanpassing(it); if (e.key === "Escape") setEditId(null); }} style={{ flex: 1, border: `1px solid ${C.pri}`, borderRadius: 8, padding: "8px 9px", fontSize: 14, fontFamily: "inherit" }} />
                      <button onClick={() => bewaarPreviewAanpassing(it)} style={{ border: 0, borderRadius: 7, background: C.pri, color: "#fff", padding: "7px 10px", fontWeight: 750, cursor: "pointer" }}>Opslaan</button>
                      <button onClick={() => setEditId(null)} style={{ border: `1px solid ${C.bdr}`, borderRadius: 7, background: C.bg2, color: C.txt2, padding: "7px 9px", fontWeight: 700, cursor: "pointer" }}>Annuleren</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 15, fontWeight: 650, color: C.txt }}>{tekstVan(it)}</div>
                      <div style={{ fontSize: 11.5, color: C.txt3, marginTop: 2 }}>
                        {mepItem ? (it.via ? `via: ${it.via}` : "uit Mise en Place") : <>{it.toegevoegd_door ? `door ${it.toegevoegd_door}` : "handmatig"}{it.toegevoegd_door && tijd ? " · " : ""}{tijd}</>}
                      </div>
                    </>
                  )}
                </div>
                {!editing && <>
                  {!mepItem && <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, padding: "2px 7px", borderRadius: 6, background: op ? C.opBg : C.bijnaBg, color: op ? C.op : C.bijna }}>{op ? "Op" : "Bijna op"}</span>}
                  <button onClick={() => startAanpassen(it)} style={{ background: C.bg2, border: `1px solid ${C.bdr}`, color: C.txt2, borderRadius: 8, padding: "6px 9px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>Aanpassen</button>
                  <button onClick={() => mepItem ? regelMep?.(it) : verwijder?.(it.id)} style={{ background: "none", border: `1px solid ${C.bdr}`, color: C.txt2, borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>Geregeld ✓</button>
                </>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ALLES-IN-ÉÉN — knop + sheet, met eigen open-state (voor Recepten)
// ─────────────────────────────────────────────────────────────────────────────
export function Whiteboard({ sb, tenantId, supabaseClient }) {
  const wb = useWhiteboard(sb, tenantId, supabaseClient);
  const [open, setOpen] = useState(false);
  return (
    <>
      <WhiteboardButton count={wb.items.length} onClick={() => setOpen(true)} />
      <WhiteboardSheet open={open} onClose={() => setOpen(false)}
        items={wb.items} artikelen={wb.artikelen} voegToe={wb.voegToe} verwijder={wb.verwijder} />
    </>
  );
}
